import * as R from 'rambdax'
import {useMemo, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import {usePrefixedParams, SetPrefixedParamsOptions} from './usePrefixedParams'
import {parseParams, unparseParams} from 'quickstart/lib/url-params'
import {logger} from 'tizra'
import {SearchConfig} from 'quickstart/lib/search/config'

const log = logger('quickstart/hooks/useSearchParams')

const useClassicParams = ({mode}: SearchConfig) => {
  const location = useLocation()

  return useMemo(() => {
    const usp = new URLSearchParams(location.search)
    return mode !== 'search'
      ? {
          params: {},
          kill: [],
        }
      : {
          params: R.filter(Boolean, {
            // XXX WITHIN_PARAM_NAME
            within: usp.get('context') === '-1' && '-1',
            terms: usp.get('searchText'),
          }),
          kill: ['context', 'searchMode', 'searchText'],
        }
  }, [mode, location])
}

/**
 * React hook for getting and setting search parameters.
 */
export const useSearchParams = (
  config: SearchConfig,
  prefix = config.mode === 'browse' ? 'browseParam-' : 'searchParam-',
) => {
  const {params: classicParams, kill} = useClassicParams(config)
  const [prefixedParams, setPrefixedParams] = usePrefixedParams(prefix)

  const params = useMemo(
    () =>
      config
        ? {
            ...classicParams,
            ...parseParams(prefixedParams, config),
          }
        : {},
    [config, classicParams, prefixedParams],
  )

  const setParams = useCallback(
    (
      params: Record<string, any>,
      options?: Omit<SetPrefixedParamsOptions, 'kill'>,
    ) => {
      if (log.assert(config, 'setParams called with null search config')) {
        setPrefixedParams(
          // @ts-expect-error unparseParams is not TypeScript yet
          unparseParams(params, config),
          {kill, ...options},
        )
      }
    },
    [config, kill, setPrefixedParams],
  )

  return [params, setParams] as const
}
